/**
 * The group layer's authoring rules — D217.
 *
 * @remarks
 * A group is a name for things a plural can name together, and the words the
 * pack says about them together (content-schema `groups.ts`). Four rules, the
 * reference walk and the D31/D47 dead-content law:
 *
 * - Group ids are unique — two groups under one id would make an item's
 *   `groupId` ambiguous.
 * - An item's `groupId` names a declared group — a typo would otherwise drop
 *   the thing out of its set's line without a word.
 * - A group needs at least two members. The engine reads a group's line only
 *   for a SET, and one thing is never a set, so a group of one could never
 *   speak.
 * - A group's description resolves its conditions like every text.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';

import { type ReferenceBrokenFailure } from './load-failure.js';
import { checkNarrativeText, declaredIdSetsOf } from './reference-walkers.js';
import { brokenReference, collectDuplicates } from './semantic-validation.js';

/**
 * Validate every group declaration and every item's membership.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validateGroupAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declared = declaredIdSetsOf(contentPack);
	const groupIds = collectDuplicates(
		contentPack.groups.map((group, groupIndex) => ({
			declarationPath: `groups[${String(groupIndex)}].groupId`,
			declaredId: group.groupId,
		})),
		'group',
		failures,
	);
	const memberCounts = new Map<string, number>();

	contentPack.items.forEach((item, itemIndex) => {
		if (item.groupId === undefined) {
			return;
		}

		memberCounts.set(item.groupId, (memberCounts.get(item.groupId) ?? 0) + 1);

		if (!groupIds.has(item.groupId)) {
			failures.push(
				brokenReference(
					`items[${String(itemIndex)}].groupId`,
					item.groupId,
					'this item joins a group the pack does not declare in `groups` — a typo here drops it out of its set\'s line without a word',
				),
			);
		}
	});

	contentPack.groups.forEach((group, groupIndex) => {
		const path = `groups[${String(groupIndex)}]`;

		if ((memberCounts.get(group.groupId) ?? 0) < 2) {
			failures.push(
				brokenReference(
					`${path}.groupId`,
					group.groupId,
					'fewer than two items join this group, and one thing is never a set, so its description could never speak',
				),
			);
		}

		checkNarrativeText(group.description, `${path}.description`, declared, failures);
	});

	return failures;
};
